// File: src/Root.js
// Purpose: Root component with code splitting and lazy loading
// Last modified: 2025-11-06

import './App.scss';
import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import NoSleep from 'nosleep.js';
import { ThemeProvider } from './theme/ThemeProvider';
import store from './store/store';
import { fetchPackages } from './store/slices/packagesSlice';
import { fetchUser } from './store/slices/userSlice';
import { fetchUserSettings } from './store/slices/settingsSlice';
import { initEncryptionKey } from './services/api';
import NotFound404 from "./components/404/NotFound404";

// ==================== Lazy Loaded Components ====================
// Code splitting: Each route is loaded only when needed
const HomeComponent = lazy(() => import('./components/home/HomeComponent'));
const Price = lazy(() => import('./components/price/Price'));
const SavedQuestions = lazy(() => import('./components/saved/SavedQuestion'));
const Profile = lazy(() => import('./components/profile/Profile'));
const Settings = lazy(() => import('./components/settings/Settings'));
const PackageSingleView = lazy(() => import('./components/view/PackageSingleView'));
const AdminUserSearch = lazy(() => import('./components/admin/AdminUserSearch'));
const Search = lazy(() => import('./components/search/Search'));
const PracticeMode = lazy(() => import('./components/practice/PracticeMode'));

// ==================== Loading Fallback ====================
const LoadingFallback = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            flexDirection: 'column',
            gap: 2,
        }}
    >
        <CircularProgress
            size={60}
            sx={{ color: '#03989F' }}
        />
        <p style={{ color: '#666', fontSize: '16px' }}>
            Yuklanmoqda...
        </p>
    </Box>
);

// ==================== Root Component ====================
function Root() {
    // Use ref to track if initial fetch has been done
    // This prevents double-fetching in React 18 Strict Mode
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        // Only fetch if we haven't already
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;

            // Check if data is already in store before fetching
            const state = store.getState();

            // Only fetch packages if not already loaded or loading
            if (!state.packages.loading && state.packages.packages.length === 0) {
                store.dispatch(fetchPackages());
            }

            // Only fetch user if not already loaded or loading
            if (!state.user.loading && !state.user.user.email) {
                store.dispatch(fetchUser()).then(result => {
                    if (result.payload?.id) store.dispatch(fetchUserSettings());
                });
            }

            initEncryptionKey();
        }

        // NoSleep setup (working only in Android)
        const timer = setTimeout(() => {
            const noSleep = new NoSleep();
            noSleep.enable();
        }, 3000);

        return () => clearTimeout(timer);
    }, []); // Empty dependency array - only run once

    return (
        <Provider store={store}>
            <ThemeProvider>
                <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                        <Route path='/' element={<HomeComponent />} />
                        <Route path='/narxlar' element={<Price />} />
                        <Route path='/saved' element={<SavedQuestions />} />
                        <Route path='/sozlanmalar' element={<Settings />} />
                        <Route path='/profil' element={<Settings />} />
                        <Route
                            path='/expired-login'
                            element={<Settings expiredLogin={true} />}
                        />
                        <Route path='/package' element={<PackageSingleView />} />
                        <Route
                            path='/admin/users/search'
                            element={<AdminUserSearch />}
                        />
                        <Route path='/izlash' element={<Search />} />
                        <Route path='/practice/:packageId' element={<PracticeMode />} />
                        <Route path='*' element={<NotFound404 />} />
                    </Routes>
                </Suspense>
            </ThemeProvider>
        </Provider>
    );
}

export default Root;
